import { Dialog } from 'primereact/dialog'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import EmptyState from '../../../components/ui/EmptyState'
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton'
import HistoryTimeline, { type TimelineEvent } from '../../../components/ui/HistoryTimeline'
import { formatDateTime } from '../../../lib/datetime'
import type { MeetingLogDetail, MeetingLogEntry } from '../../meetings/types'
import { useActionLogs } from '../hooks/useActionLogs'
import '../../meetings/meetings.css'

interface Props {
  actionId: number
  visible: boolean
  onHide: () => void
  /**
   * Pencere başlığı. Verilmezse ana görevdeki metin ("İşlem Geçmişi") kullanılır; alt görev ekranı
   * kapsamı belirtmek için kendi başlığını geçer (kullanıcı isteği 24.09.2026).
   */
  title?: string
}

/**
 * Aksiyon İşlem Geçmişi (074) — bir aksiyonun (ve alt aksiyonlarının) denetim kayıtlarını zaman tüneli olarak
 * gösterir (en yeni önce). Her olay: işlem adı (başlık) + kullanıcı·zaman (meta) + LOG_INFO'dan çözülmüş
 * değişiklik satırları ("Statü: eski → yeni"). Statü id'leri backend'de ada çözülür. Paylaşılan
 * {@link HistoryTimeline} + toplantı geçmişi deseni yeniden kullanılır (Reuse Mandate). Veri yalnız dialog açıkken.
 */
export default function ActionHistoryDialog({ actionId, visible, onHide, title }: Readonly<Props>) {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useActionLogs(actionId, visible)

  const events: TimelineEvent[] = (data ?? []).map((entry, index) => ({
    title: entry.operationName,
    meta: [actorLabel(entry, t), formatDateTime(entry.creDate)].filter(Boolean).join(' · '),
    details: entry.details.length > 0 ? <DetailLines entry={entry} /> : undefined,
    // Liste en-yeni-önce → ilk kayıt en son işlem; yeşil vurguyla ayrışır.
    highlight: index === 0,
  }))

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={title ?? t('actions.detail.historyDialog.title')}
      modal
      dismissableMask
      style={{ width: '40rem', maxWidth: '95vw' }}
      className="mtg-history-dialog"
    >
      {isLoading && <LoadingSkeleton lines={4} />}
      {!isLoading && isError && (
        <EmptyState icon="pi pi-exclamation-triangle" title={t('actions.detail.historyDialog.loadError')} />
      )}
      {!isLoading && !isError && events.length === 0 && (
        <EmptyState icon="pi pi-history" title={t('actions.detail.historyDialog.empty')} />
      )}
      {!isLoading && !isError && events.length > 0 && <HistoryTimeline events={events} />}
    </Dialog>
  )
}

/** Bir olayın LOG_INFO değişiklik satırları: "Alan: eski → yeni" ya da özet "Alan: değer". */
function DetailLines({ entry }: Readonly<{ entry: MeetingLogEntry }>) {
  const { t } = useTranslation()
  const label = (key: string) => t(`actions.detail.historyDialog.field.${key}`, { defaultValue: key })
  return (
    <ul className="mtg-history-changes">
      {entry.details.map((d: MeetingLogDetail, i) => (
        <li key={i}>
          <span className="mtg-history-field">{label(d.key)}:</span>{' '}
          {d.oldValue != null ? (
            <span>
              <span className="mtg-history-old">{d.oldValue}</span>{' '}
              <span className="mtg-history-arrow">{t('actions.detail.historyDialog.changeTo')}</span>{' '}
              <span className="mtg-history-new">{d.newValue ?? '—'}</span>
            </span>
          ) : (
            <span className="mtg-history-new">{d.newValue ?? '—'}</span>
          )}
        </li>
      ))}
    </ul>
  )
}

/**
 * Meta satırındaki kişi etiketi. Vekil olarak yapılmış bir işlemde tek bir ad yetmez: kaydın sahibi
 * (adına çalışılan) ile işlemi fiilen yapan farklıdır ve denetim izinin değeri tam olarak bu ayrımdadır
 * (089). Vekil bilgisi yoksa bugünkü metin birebir korunur.
 */
function actorLabel(entry: MeetingLogEntry, t: TFunction): string | null {
  if (!entry.actingUserName) return entry.userName
  return t('proxy.history.onBehalf', { owner: entry.userName ?? '', actor: entry.actingUserName })
}
